// Mapping option `carryForwardOnContinue`: whether "Valider et continuer" pre-fills
// the next container with the values of the container just recorded. Read by the
// mapping screen and the operation screens (see components/operation/utils/carryForward
// for the rules). In every mode only values entered on the container are carried (or
// carried into it, unchanged). Values:
// - false: not carried;
// - true: carried, replacing the next container's values;
// - 'ifEmpty': carried only into a next container without the group's lead measure
//   (its TAV, its volume), whose values for the group are then replaced.

export const CARRY_FORWARD_MODES = {
  off: 'off',
  ifEmpty: 'ifEmpty',
  replace: 'replace',
};

const SETTING_BY_MODE = {
  [CARRY_FORWARD_MODES.off]: false,
  [CARRY_FORWARD_MODES.ifEmpty]: 'ifEmpty',
  [CARRY_FORWARD_MODES.replace]: true,
};

// Measures are carried as a group, set on the group's mapping column: a TAV goes with
// its temperature, a volume temperature with the volume measurement. leadFormKeys hold
// the measure that decides whether the next container already has the group: a stored
// temperature alone (e.g. a constant 20 °C from the CSV) does not count, so a TAV and
// its temperature always come from the same container. The volume measurement itself
// is not carried; a full container takes its volume from the capacity, so its volume
// temperature is its only volume measure (fullContainerLeadFormKeys). Form keys match
// buildMappedUpdates in operation/utils/operationUtils.
export const STANDARD_CARRY_FORWARD_GROUPS = {
  tav: { formKeys: ['tav', 'tavTemp'], leadFormKeys: ['tav'] },
  volumeTemperature: {
    formKeys: ['volumeTemp'],
    leadFormKeys: ['liquidVolume', 'liquidHeight', 'emptyHeight'],
    fullContainerLeadFormKeys: ['volumeTemp'],
  },
};

/**
 * @param {string} settingKey - a STANDARD_CARRY_FORWARD_GROUPS key or a custom field id
 * @param {boolean|string|undefined} carryForwardOnContinue - its setting, undefined when never set
 * @returns {string} one of CARRY_FORWARD_MODES; measures are replaced by default
 */
export const carryForwardMode = (settingKey, carryForwardOnContinue) => {
  if (carryForwardOnContinue === undefined) {
    return Object.prototype.hasOwnProperty.call(STANDARD_CARRY_FORWARD_GROUPS, settingKey)
      ? CARRY_FORWARD_MODES.replace
      : CARRY_FORWARD_MODES.off;
  }
  if (carryForwardOnContinue === SETTING_BY_MODE.ifEmpty) {
    return CARRY_FORWARD_MODES.ifEmpty;
  }
  return carryForwardOnContinue === true ? CARRY_FORWARD_MODES.replace : CARRY_FORWARD_MODES.off;
};

export const carryForwardSetting = (mode) => SETTING_BY_MODE[mode] ?? false;

// Keeps a stored setting valid, for normalizers that used to coerce it to a boolean
export const normalizeCarryForwardOnContinue = (value) => (
  value === SETTING_BY_MODE.ifEmpty ? value : value === true
);

// carryForwardMode for every key, read from mapping columns
export const carryForwardModeOfColumns = (columns) => (settingKey) => carryForwardMode(
  settingKey,
  columns?.[settingKey]?.carryForwardOnContinue,
);
